/**
 * Batch B — warm critical React Query caches while online so offline read
 * has something to show (RQ IndexedDB persist — NOT Workbox auth GET cache).
 */
import type { QueryClient } from "@tanstack/react-query";
import {
  acceptedQuotesTotalQueryOptions,
  activePhaseCountQueryOptions,
  activePhaseProgressQueryOptions,
  budgetByPhaseQueryOptions,
  nextTasksQueryOptions,
  spentTotalQueryOptions,
  tasksActualBudgetTotalQueryOptions
} from "@/lib/api/dashboard";
import { documentsQueryOptions } from "@/lib/api/documents";
import { phasesWithStatsQueryOptions } from "@/lib/api/phases";
import { projectQueryOptions } from "@/lib/api/project";
import { scheduleQueryOptions } from "@/lib/api/schedule";
import { taskTreeQueryOptions } from "@/lib/api/tasks";

export async function warmCriticalQueries(client: QueryClient): Promise<void> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return;

  try {
    const project = await client.fetchQuery(projectQueryOptions());
    const projectId = project?.id;

    await Promise.allSettled([
      client.prefetchQuery(phasesWithStatsQueryOptions(projectId)),
      client.prefetchQuery(documentsQueryOptions()),
      client.prefetchQuery(scheduleQueryOptions(projectId)),
      client.prefetchQuery(taskTreeQueryOptions()),
      client.prefetchQuery(spentTotalQueryOptions()),
      client.prefetchQuery(activePhaseProgressQueryOptions()),
      client.prefetchQuery(nextTasksQueryOptions(5)),
      client.prefetchQuery(budgetByPhaseQueryOptions()),
      client.prefetchQuery(acceptedQuotesTotalQueryOptions()),
      client.prefetchQuery(tasksActualBudgetTotalQueryOptions()),
      client.prefetchQuery(activePhaseCountQueryOptions())
    ]);
  } catch {
    // Warm is best-effort — never block the app shell
  }
}
